import React, { createContext, useCallback, useContext, useEffect } from 'react'
import { useMsal } from '@azure/msal-react'
import { useNavigate } from 'react-router'
import { CustomNavigationClient } from '../utils/NavigationClient'
import {
  AuthenticationResult,
  BrowserAuthError,
  EventMessage,
  EventType,
  InteractionStatus,
  SilentRequest,
} from '@azure/msal-browser'
import apiClient from '../../../services/api-client'
import { useAppDispatch } from '../../../store/rootStore'
import AuthSlice, {
  AuthAccount,
  getAccounts,
  selectAllFarmerAccounts,
  selectCurrentAccountId,
  selectCurrentFarmerAccount,
  selectToken,
} from '../storage'
import Analytics from '../../../services/analytics'
import { useSelector } from 'react-redux'
import { getStores } from '../../stores/storage/stores.slice'
import { Capacitor } from '@capacitor/core'
import AppUrlListener from '../../../native/utils/AppUrlListener'
import { useOnlineStatus } from '../../../util/useOnlineStatus'
import {
  NavigationAuthenticationClient,
  instantiateNativeClient,
} from '../../../native/utils/NavigationClient'
import { Loader } from '../../../components/Loader'
import { Browser } from '@capacitor/browser'
import { Typography } from '@mui/material'
import useAppInfo from '../../../native/utils/AppInfo'
import { useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/browser'

interface MsalRouterProps {
  element: React.ReactNode
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const emptyFunc = () => {}
interface AuthContext {
  logout: () => void
  isAuthenticated: boolean
  isShareholder: boolean
  hasShareholder: boolean
}

export const AuthContext = createContext<AuthContext>({
  logout: emptyFunc,
  isAuthenticated: false,
  isShareholder: false,
  hasShareholder: false,
})

export const useIsAuthenticated = () => {
  const context = useContext(AuthContext)

  return context.isAuthenticated
}

export const useIsShareholder = () => {
  const context = useContext(AuthContext)

  return context.isShareholder
}

export const useHasShareholderAccounts = () => {
  const context = useContext(AuthContext)

  return context.hasShareholder
}

export const useLogout = () => {
  const context = useContext(AuthContext)

  return context.logout
}

const Outter: React.FC<MsalRouterProps> = (props) => {
  const { instance, inProgress, accounts } = useMsal()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const attemptLoginAgain = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      instance.loginRedirect(createTokenRequest('login')).catch(console.log)
    } else {
      sessionStorage.removeItem('msal.interaction.status')
      instance.acquireTokenRedirect(createTokenRequest('login')).catch(console.log)
    }
  }, [instance])

  const tokenCallback = React.useCallback(async () => {
    if (accounts.length > 0 && inProgress === InteractionStatus.None) {
      if (!online && _token) {
        return _token
      }

      return instance
        .acquireTokenSilent(createTokenRequest())
        .then((accessTokenResponse) => {
          return accessTokenResponse.accessToken
        })
        .catch((error: BrowserAuthError) => {
          if (error.errorCode !== 'no_network_connectivity') {
            attemptLoginAgain()
          }
        })
    } else {
      instance.loginRedirect(createTokenRequest('login'))
    }
    return null
  }, [accounts, inProgress, attemptLoginAgain])

  useEffect(() => {
    if (accounts.length > 0 && inProgress === InteractionStatus.None) {
      const timeoutId = setTimeout(() => {
        instance.loginRedirect(createLoginTokenRequest('login'))
      }, 10000)
      tokenCallback()
        .then((token) => {
          if (token) {
            clearTimeout(timeoutId)
            apiClient.setDefaultToken(token)
            apiClient.setGetAccessToken(tokenCallback)
            dispatch(AuthSlice.actions.setToken(token))
            setInitialized(true)
          }
        })
        .catch((error) => {
          console.log('Token callback rejected')
          console.log(error)
        })
    }
  }, [accounts, inProgress, tokenCallback])

  // Low level event callbacks
  useEffect(() => {
    const callbackId = instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult
        const account = payload.account
        instance.setActiveAccount(account)
      }

      if (!Capacitor.isNativePlatform()) {
        if (
          event.eventType === EventType.LOGIN_FAILURE &&
          event.error?.message.includes('AADB2C90118')
        ) {
          sessionStorage.clear()
          instance.loginRedirect({
            scopes: [],
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            authority: process.env.REACT_APP_AZURE_B2C_AUTHORITY_PASSWORD_RESET!,
          })
        }
      }
      // else if (Capacitor.isNativePlatform()) {
      //   if (event.eventType === EventType.ACQUIRE_TOKEN_NETWORK_START) {
      //     // If this fails to open within 5 seconds.
      //     setTimeout(() => {
      //       sessionStorage.clear()
      //       instance.loginRedirect(createLoginTokenRequest())
      //     }, 5000)
      //   }
      // }
    })

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [])

  const createTokenRequest = (prompt = 'none') => {
    const accessTokenRequest: SilentRequest = {
      scopes: [`${process.env.REACT_APP_REQUEST_API_SCOPES}`],
      forceRefresh: true,

      account: accounts.find((a) => a.homeAccountId === defaultAuthAccount) ?? accounts[0],
      prompt: prompt,
    }

    return accessTokenRequest
  }

  const [initialized, setInitialized] = React.useState(false)

  const defaultAuthAccount = useSelector(selectCurrentAccountId)

  const online = useOnlineStatus()
  const _token = useSelector(selectToken)

  const createLoginTokenRequest = (prompt = 'login') => {
    const accessTokenRequest: SilentRequest = {
      scopes: [`${process.env.REACT_APP_REQUEST_API_SCOPES}`],
      account:
        accounts.find((a) => a.homeAccountId === defaultAuthAccount) ?? accounts[0] ?? undefined,
      prompt: prompt,
    }

    return accessTokenRequest
  }

  const callbackNativeRedirectAuth = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (slug: string, url: string) => {
      if (!url) {
        return
      }
      if (Capacitor.isNativePlatform()) {
        Browser.close()
      }

      const uri = new URL(url)
      console.log('Received navigation callback for: ', uri)
      // Do not forward msauth redirects.
      const pathname =
        uri.origin.includes('msauth') || uri.pathname.includes('//nz.co.farmlands')
          ? '/'
          : uri.pathname
      const href = uri.hash.length > 1 ? uri.hash : ''
      const params = '?' + uri.searchParams
      console.log('Recalculated navigation from local to ', pathname + href + params)
      navigate(pathname + href + params, { replace: true })
    },
    [instance],
  )

  const [closeBrowserTimeout, setCloseBrowserTimeout] = React.useState<NodeJS.Timeout>()
  const [browserState, setBrowserState] = React.useState<'reset' | 'login'>()

  useEffect(() => {
    Browser.addListener('browserFinished', () => {
      if (accounts.length <= 0) {
        if (browserState !== 'reset') {
          if (window.location.hash.includes('AADB2C90118')) {
            sessionStorage.removeItem('msal.interaction.status')
            setBrowserState('reset')
            const timeout = setTimeout(() => {
              instance.loginRedirect({
                scopes: [],
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                authority: process.env.REACT_APP_AZURE_B2C_AUTHORITY_PASSWORD_RESET!,
              })
            }, 1000)

            return () => {
              clearTimeout(timeout)
              Browser.removeAllListeners()
            }
          } else if (browserState === 'login') {
            const timeout = setTimeout(() => {
              sessionStorage.removeItem('msal.interaction.status')
              instance.loginRedirect(createLoginTokenRequest())
            }, 3000)

            setCloseBrowserTimeout(timeout)

            return () => {
              clearTimeout(timeout)
              Browser.removeAllListeners()
            }
          }
        } else if (browserState === 'reset') {
          setBrowserState(undefined)
          window.location.href = ''
        }
      }
    })

    return () => {
      Browser.removeAllListeners()
    }
  }, [accounts, browserState])

  useEffect(() => {
    const _Location = Capacitor.isNativePlatform() ? location : window.location
    // Forgot Password
    if (_Location.hash.includes('AADB2C90118')) {
      sessionStorage.removeItem('msal.interaction.status')
      clearTimeout(closeBrowserTimeout)
      setBrowserState('reset')
      setTimeout(() => {
        instance.loginRedirect({
          scopes: [],
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          authority: process.env.REACT_APP_AZURE_B2C_AUTHORITY_PASSWORD_RESET!,
        })
      }, 1000)
      // Interaction Required
    } else if (_Location.hash.includes('AADB2C90077')) {
      sessionStorage.removeItem('msal.interaction.status')
      clearTimeout(closeBrowserTimeout)
      instance.logout()
      setBrowserState('login')
    } else if (_Location.hash.includes('state=')) {
      const slug = `#state=${_Location.hash.split('#state=').pop()?.replace('?', '')}`
      if (slug) {
        console.log(
          'Handling native redirect promise with this: ',
          process.env.REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI + slug,
        )
        clearTimeout(closeBrowserTimeout)
        instance
          .handleRedirectPromise(process.env.REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI + slug)
          .then((result) => {
            if (result !== null) {
              console.log('Native has authenticated')
            } else {
              console.log('Result is null', result)
            }
          })
          .catch((err) => {
            sessionStorage.clear()
            console.error('Error in native callback: ')
            console.error(err)
          })
      }
    } else if (accounts.length <= 0 && inProgress == InteractionStatus.None) {
      clearTimeout(closeBrowserTimeout)
      setBrowserState('login')
      instance.loginRedirect(createLoginTokenRequest())
    } else if (accounts.length > 0) {
      clearTimeout(closeBrowserTimeout)
      setBrowserState(undefined)
    }
  }, [location, inProgress, accounts, closeBrowserTimeout])

  const { appInfoString } = useAppInfo()

  return (
    <>
      {Capacitor.isNativePlatform() && <AppUrlListener callback={callbackNativeRedirectAuth} />}
      {initialized ? (
        <Inner {...props} />
      ) : (
        <>
          <Loader title={browserState === 'reset' ? 'Forgot password' : 'Authenticating'} />
          <Typography textAlign='center' variant='caption'>
            {appInfoString}
          </Typography>
        </>
      )}
    </>
  )
}
const MsalAuthenticatedRouter: React.FC<MsalRouterProps> = (props) => {
  const { instance } = useMsal()
  const navigate = useNavigate()

  const navigationClientWeb = new CustomNavigationClient(navigate)
  const navigationClient = new NavigationAuthenticationClient(instance)
  const [navigationSet, setNavigationSet] = React.useState(false)

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      instantiateNativeClient(navigationClient)
      instance.setNavigationClient(navigationClient)
    } else {
      instance.setNavigationClient(navigationClientWeb)
    }
    setNavigationSet(true)
  }, [])

  if (navigationSet) {
    return <Outter {...props} />
  }
}

const Inner: React.FC<MsalRouterProps> = (props) => {
  const { instance, accounts } = useMsal()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const defaultAuthAccount = useSelector(selectCurrentAccountId)
  const farmerAccounts = useSelector(selectCurrentFarmerAccount)
  const digitalFarmerState = useSelector(selectAllFarmerAccounts)

  const _logout = useCallback(() => {
    apiClient.tokenInterceptorId = null
    dispatch({
      type: 'LOGOUT_REQUEST',
    })
    Analytics.push({
      dataLayer: {
        event: 'LOGOUT_REQUEST',
      },
    })

    instance.logout().catch((error) => {
      Analytics.push({
        dataLayer: {
          error: error,
          event: 'LOGOUT_ERROR',
        },
      })
    })

    navigate('/home')

    if (Capacitor.isNativePlatform()) {
      const reload = window.location.href
      window.location.href = reload
    }
  }, [instance])

  useEffect(() => {
    if (accounts.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(AuthSlice.actions.setAuthAccount(accounts[0] as any as AuthAccount))

      let email = accounts[0].username
      if (accounts[0].idTokenClaims?.emails) {
        // Not actually an array, just poor typing from microsoft
        email = accounts[0].idTokenClaims.emails as unknown as string
      }
      Sentry.setUser({
        email: email,
        username: email,
      })
    }
  }, [accounts])

  useEffect(() => {
    if (
      accounts.length > 0 &&
      defaultAuthAccount !== null &&
      apiClient.tokenInterceptorId !== null
    ) {
      dispatch(getAccounts())
      dispatch(getStores())
    }
  }, [apiClient.tokenInterceptorId, defaultAuthAccount, accounts])

  const context = React.useMemo(() => {
    return {
      isAuthenticated: !!farmerAccounts,
      isShareholder: farmerAccounts?.hasShareholderAccess ?? false,
      hasShareholder: digitalFarmerState?.hasShareholderAccounts ?? false,
      logout: _logout,
    }
  }, [farmerAccounts, digitalFarmerState, _logout])

  const renderApplication = () => {
    return <AuthContext.Provider value={context}>{props.element}</AuthContext.Provider>
  }

  return renderApplication()
}

export default MsalAuthenticatedRouter
